import Heading from 'components/Basic/Heading/Heading';
import { useComponentUpdate } from 'hooks/helpers/UseComponentUpdate';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useGetWindowSize } from 'hooks/ui/UseGetWindowSize';
import { ArrowIcon } from 'public/svg/IconsSvg';
import { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

type CollapseProps = {
    mobileTitle: string;
    parentCounter?: number;
    parentRef?: RefObject<HTMLDivElement>;
    parentState?: boolean;
    setParentState?: Dispatch<SetStateAction<boolean>>;
};

const Collapse: FC<CollapseProps> = ({
    children,
    mobileTitle,
    parentCounter,
    parentRef,
    parentState,
    setParentState,
}) => {
    const t = useTypedTranslationFunction();
    const testIdentifier = 'basic-collapse';
    const ref = useRef<HTMLDivElement>(null);
    const wrapperRef = parentRef ?? ref;
    const [isCollapsed, setIsCollapsed] = useState(parentState ?? false);
    const windowSize = useGetWindowSize(true);
    const [height, setHeight] = useState<number>();

    const setCollapseStates = useCallback<Dispatch<SetStateAction<boolean>>>(
        (state) => {
            setIsCollapsed((prev) => {
                const result = typeof state === 'function' ? state(prev) : state;
                if (setParentState) {
                    setParentState(result);
                }

                return result;
            });
        },
        [setParentState],
    );

    useEffect(() => {
        if (parentState !== undefined) {
            setIsCollapsed(parentState);
        }
    }, [parentState]);

    useComponentUpdate(() => {
        setHeight(wrapperRef.current?.scrollHeight);
    }, [parentCounter, wrapperRef]);

    useComponentUpdate(() => {
        setHeight(wrapperRef.current?.scrollHeight);
    }, [windowSize?.width, wrapperRef]);

    const calculatedHeight = height ?? wrapperRef.current?.scrollHeight;

    return (
        <div className="-mx-5 border-t border-solid border-borderLight px-5 vl:border-t-0" data-testid={testIdentifier}>
            <Heading
                type="h3"
                onClick={() => setCollapseStates((prev) => !prev)}
                className="mb-0 flex cursor-pointer items-center justify-between px-0 py-5 font-bold leading-6 text-primary vl:hidden"
                aria-label={isCollapsed ? `${t('Open(verb)')} ${mobileTitle}` : `${t('Close(verb)')} ${mobileTitle}`}
            >
                {mobileTitle}
                <ArrowIcon className={twJoin('h-4 w-4 text-grayDark', isCollapsed && 'rotate-180')} />
            </Heading>
            <div
                className="notVl:h-0 notVl:overflow-hidden notVl:transition-height notVl:duration-300"
                style={{ height: isCollapsed ? calculatedHeight : undefined }}
            >
                <div className="pb-15 pt-3" ref={wrapperRef}>
                    {children}
                </div>
            </div>
        </div>
    );
};

export default Collapse;
